import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { AssistantEventType } from '../../lib/types';
import type { AssistantIntent } from '../../lib/assistantIntents';
import { matchIntents, type IntentMatch } from './intentMatch';

// Intents cuya ejecución tiene un efecto en otra parte del panel (badges, listas).
// El servidor hace la mutación; aquí solo avisamos a la UI para que se refresque.
const UI_SIDE_EFFECTS: Record<string, string> = {
  'mark-leads-read': 'submissions:read',
};

/**
 * Estados del composer (sección 7.5). En Fase 2 el composer además ejecuta
 * tareas reales: captura el parámetro cuando hace falta ('collecting'), pide
 * confirmación según el intent y ejecuta la mutación ('executing'), dejando
 * traza en la bitácora y telemetría de uso.
 */
export type ComposerState =
  | 'idle'
  | 'focused'
  | 'suggesting'
  | 'collecting'
  | 'routing'
  | 'confirming'
  | 'executing'
  | 'done'
  | 'failed';

export type ComposerPlatform = 'mobile' | 'desktop';

const HISTORY_LIMIT = 4;

// Telemetría best-effort: nunca debe romper el flujo del usuario.
function track(eventType: AssistantEventType, intentId?: string, query?: string) {
  api.assistant.recordEvent({ eventType, intentId, query }).catch(() => { /* ignore */ });
}

export function useComposer(platform: ComposerPlatform) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [state, setState] = useState<ComposerState>('idle');
  const [history, setHistory] = useState<string[]>([]);
  const [pendingIntent, setPendingIntent] = useState<AssistantIntent | null>(null);
  const [pendingParam, setPendingParam] = useState<string | undefined>(undefined);
  const [feedback, setFeedback] = useState<string | null>(null);

  const matches: IntentMatch[] = useMemo(
    () => (value.trim().length > 1 ? matchIntents(value, platform) : []),
    [value, platform],
  );

  const focus = useCallback(() => {
    setState((current) => (current === 'idle' ? 'focused' : current));
  }, []);

  const blur = useCallback(() => {
    setState((current) => (current === 'focused' || current === 'suggesting' ? 'idle' : current));
  }, []);

  const change = useCallback((next: string) => {
    setValue(next);
    setFeedback(null);
    setState(next.trim().length > 1 ? 'suggesting' : 'focused');
  }, []);

  const rememberQuery = useCallback((query: string) => {
    setHistory((prev) => [query, ...prev.filter((item) => item !== query)].slice(0, HISTORY_LIMIT));
  }, []);

  const finishDone = useCallback((message: string | null) => {
    setState('done');
    setValue('');
    setPendingIntent(null);
    setPendingParam(undefined);
    setFeedback(message);
    window.setTimeout(() => setState('idle'), 1600);
  }, []);

  // Solo enruta al deep-link del intent. Sin mutación de datos.
  const routeTo = useCallback(
    (intent: AssistantIntent) => {
      setState('routing');
      const target = intent.destination?.value ?? intent.handler?.value ?? '/';
      window.setTimeout(() => {
        navigate(target);
        track('routed', intent.id);
        finishDone(null);
      }, 160);
    },
    [navigate, finishDone],
  );

  // Ejecuta la mutación real en el servidor: mutación + bitácora en una sola
  // petición atómica. El servidor es la fuente de verdad del resumen y la traza.
  const execute = useCallback(
    async (intent: AssistantIntent, param?: string) => {
      setState('executing');
      try {
        const result = await api.assistant.execute({ intentId: intent.id, param });
        track('executed', intent.id);
        const sideEffect = UI_SIDE_EFFECTS[intent.id];
        if (sideEffect) window.dispatchEvent(new Event(sideEffect));
        window.dispatchEvent(new Event('activity:changed'));
        const base = intent.successMessage ?? result.summary;
        finishDone(
          result.needsPublish ? `${base} Recuerda publicar para que se vea en tu sitio.` : base,
        );
      } catch {
        track('failed', intent.id);
        setState('failed');
        setFeedback(intent.failureMessage ?? 'No se pudo completar la acción. Inténtalo de nuevo.');
        setPendingIntent(null);
        setPendingParam(undefined);
      }
    },
    [finishDone],
  );

  // Decide el siguiente paso de un intent: ruteo, captura, confirmación o ejecución.
  const proceed = useCallback(
    (intent: AssistantIntent, param?: string) => {
      if (intent.execution.mode === 'route') {
        routeTo(intent);
        return;
      }
      if (intent.execution.param && param === undefined) {
        setPendingIntent(intent);
        setState('collecting');
        return;
      }
      if (intent.requiresConfirmation) {
        setPendingIntent(intent);
        setPendingParam(param);
        setState('confirming');
        return;
      }
      execute(intent, param);
    },
    [routeTo, execute],
  );

  const runIntent = useCallback(
    (intent: AssistantIntent, query?: string) => {
      if (query) rememberQuery(query);
      track('selected', intent.id, query);
      proceed(intent);
    },
    [rememberQuery, proceed],
  );

  const submit = useCallback(() => {
    const query = value.trim();
    if (query.length === 0) return;
    const top = matchIntents(query, platform, 1)[0]?.intent ?? null;
    if (!top) {
      rememberQuery(query);
      track('no_match', undefined, query);
      setState('failed');
      setFeedback('No reconocí esa tarea todavía. Prueba con una de las sugerencias.');
      return;
    }
    runIntent(top, query);
  }, [value, platform, rememberQuery, runIntent]);

  // Confirma el valor capturado en 'collecting' y avanza a confirmación/ejecución.
  const submitParam = useCallback(
    (param: string) => {
      if (!pendingIntent) return;
      proceed(pendingIntent, param.trim());
    },
    [pendingIntent, proceed],
  );

  const confirm = useCallback(() => {
    if (pendingIntent) execute(pendingIntent, pendingParam);
  }, [pendingIntent, pendingParam, execute]);

  const cancel = useCallback(() => {
    if (pendingIntent) track('abandoned', pendingIntent.id);
    setPendingIntent(null);
    setPendingParam(undefined);
    setState(value.trim().length > 1 ? 'suggesting' : 'idle');
  }, [pendingIntent, value]);

  return {
    value,
    state,
    matches,
    history,
    pendingIntent,
    feedback,
    focus,
    blur,
    change,
    submit,
    runIntent,
    submitParam,
    confirm,
    cancel,
  };
}
